import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
  Campaign as CampaignIcon,
  People as PeopleIcon,
  SmartToy as SmartToyIcon,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';

interface AICampaign {
  id: string;
  name: string;
  description: string;
  status: 'ativa' | 'pausada' | 'concluída';
  targetAudience: string[];
  startDate: Date;
  endDate: Date;
  leads: string[];
  aiStrategy: {
    type: 'personalização' | 'segmentação' | 'otimização';
    parameters: Record<string, any>;
  };
}

interface AILead {
  id: string;
  name: string;
  email: string;
  phone: string;
  status: 'novo' | 'contatado' | 'convertido' | 'perdido';
  source: string;
  lastContact: Date;
  aiScore: number;
  aiInsights: string[];
}

const MkIAPage: React.FC = () => {
  const [campaigns, setCampaigns] = useState<AICampaign[]>([]);
  const [leads, setLeads] = useState<AILead[]>([]);
  const [openCampaignDialog, setOpenCampaignDialog] = useState(false);
  const [openLeadDialog, setOpenLeadDialog] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<AICampaign | null>(null);
  const [selectedLead, setSelectedLead] = useState<AILead | null>(null);
  const [loading, setLoading] = useState(false);
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    description: '',
    status: 'ativa' as AICampaign['status'],
    targetAudience: [] as string[],
    startDate: new Date(),
    endDate: new Date(),
    aiStrategy: {
      type: 'personalização' as AICampaign['aiStrategy']['type'],
      parameters: {},
    },
  });
  const [leadForm, setLeadForm] = useState({
    name: '',
    email: '',
    phone: '',
    status: 'novo' as AILead['status'],
    source: '',
  });

  const products = useSelector((state: RootState) => state.products.items);

  const handleCreateCampaign = () => {
    setSelectedCampaign(null);
    setCampaignForm({
      name: '',
      description: '',
      status: 'ativa',
      targetAudience: [],
      startDate: new Date(),
      endDate: new Date(),
      aiStrategy: {
        type: 'personalização',
        parameters: {},
      },
    });
    setOpenCampaignDialog(true);
  };

  const handleEditCampaign = (campaign: AICampaign) => {
    setSelectedCampaign(campaign);
    setCampaignForm({
      name: campaign.name,
      description: campaign.description,
      status: campaign.status,
      targetAudience: campaign.targetAudience,
      startDate: campaign.startDate,
      endDate: campaign.endDate,
      aiStrategy: campaign.aiStrategy,
    });
    setOpenCampaignDialog(true);
  };

  const handleSaveCampaign = () => {
    if (selectedCampaign) {
      setCampaigns(campaigns.map(c => 
        c.id === selectedCampaign.id 
          ? { ...c, ...campaignForm }
          : c
      ));
    } else {
      setCampaigns([...campaigns, {
        id: Date.now().toString(),
        ...campaignForm,
        leads: [],
      }]);
    }
    setOpenCampaignDialog(false);
  };

  const handleDeleteCampaign = (campaignId: string) => {
    setCampaigns(campaigns.filter(c => c.id !== campaignId));
  };

  const handleCreateLead = () => {
    setSelectedLead(null);
    setLeadForm({
      name: '',
      email: '',
      phone: '',
      status: 'novo',
      source: '',
    });
    setOpenLeadDialog(true);
  };

  const handleEditLead = (lead: AILead) => {
    setSelectedLead(lead);
    setLeadForm({
      name: lead.name,
      email: lead.email,
      phone: lead.phone,
      status: lead.status,
      source: lead.source,
    });
    setOpenLeadDialog(true);
  };

  const handleSaveLead = () => {
    if (selectedLead) {
      setLeads(leads.map(l => 
        l.id === selectedLead.id 
          ? { ...l, ...leadForm, lastContact: new Date() }
          : l
      ));
    } else {
      setLeads([...leads, {
        id: Date.now().toString(),
        ...leadForm,
        lastContact: new Date(),
        aiScore: Math.random() * 100,
        aiInsights: ['Insight 1', 'Insight 2'],
      }]);
    }
    setOpenLeadDialog(false);
  };

  const handleDeleteLead = (leadId: string) => {
    setLeads(leads.filter(l => l.id !== leadId));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ativa':
        return 'success';
      case 'pausada':
        return 'warning';
      case 'concluída':
        return 'info';
      case 'novo':
        return 'primary';
      case 'contatado':
        return 'warning';
      case 'convertido':
        return 'success';
      case 'perdido':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleGenerateAIInsights = async (leadId: string) => {
    setLoading(true);
    // Simular chamada à API de IA
    await new Promise(resolve => setTimeout(resolve, 2000));
    setLeads(leads.map(l => 
      l.id === leadId 
        ? { 
            ...l, 
            aiScore: Math.random() * 100,
            aiInsights: [
              'Tendência de compra alta',
              'Interesse em produtos premium',
              'Melhor horário de contato: manhã'
            ]
          }
        : l
    ));
    setLoading(false);
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h4">
          Mk.IA - Marketing com Inteligência Artificial
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            startIcon={<CampaignIcon />}
            onClick={handleCreateCampaign}
          >
            Nova Campanha IA
          </Button>
          <Button
            variant="contained"
            startIcon={<PeopleIcon />}
            onClick={handleCreateLead}
          >
            Novo Lead IA
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Campanhas IA */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Campanhas IA Ativas
            </Typography>
            <List>
              {campaigns.map((campaign) => (
                <React.Fragment key={campaign.id}>
                  <ListItem>
                    <ListItemText
                      primary={campaign.name}
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {campaign.description}
                          </Typography>
                          <Box display="flex" gap={1} mt={1}>
                            <Chip
                              label={campaign.status}
                              color={getStatusColor(campaign.status)}
                              size="small"
                            />
                            <Chip
                              label={campaign.aiStrategy.type}
                              color="primary"
                              size="small"
                            />
                            <Typography variant="caption" color="text.secondary">
                              {campaign.leads.length} leads
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => handleEditCampaign(campaign)}
                        sx={{ mr: 1 }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        edge="end"
                        onClick={() => handleDeleteCampaign(campaign.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Leads IA */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Leads IA
            </Typography>
            <List>
              {leads.map((lead) => (
                <React.Fragment key={lead.id}>
                  <ListItem>
                    <ListItemText
                      primary={lead.name}
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {lead.email} • {lead.phone}
                          </Typography>
                          <Box display="flex" gap={1} mt={1}>
                            <Chip
                              label={lead.status}
                              color={getStatusColor(lead.status)}
                              size="small"
                            />
                            <Chip
                              label={`Score IA: ${lead.aiScore.toFixed(1)}`}
                              color="primary"
                              size="small"
                            />
                            <Typography variant="caption" color="text.secondary">
                              Fonte: {lead.source}
                            </Typography>
                          </Box>
                          {lead.aiInsights && lead.aiInsights.length > 0 && (
                            <Box mt={1}>
                              <Typography variant="caption" color="text.secondary">
                                Insights IA:
                              </Typography>
                              <Box display="flex" flexWrap="wrap" gap={1}>
                                {lead.aiInsights.map((insight, index) => (
                                  <Chip
                                    key={index}
                                    label={insight}
                                    size="small"
                                    variant="outlined"
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => handleGenerateAIInsights(lead.id)}
                        disabled={loading}
                        sx={{ mr: 1 }}
                      >
                        {loading ? <CircularProgress size={24} /> : <SmartToyIcon />}
                      </IconButton>
                      <IconButton
                        edge="end"
                        onClick={() => handleEditLead(lead)}
                        sx={{ mr: 1 }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        edge="end"
                        onClick={() => handleDeleteLead(lead.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>

      {/* Dialog para Campanha IA */}
      <Dialog
        open={openCampaignDialog}
        onClose={() => setOpenCampaignDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedCampaign ? 'Editar Campanha IA' : 'Nova Campanha IA'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Nome da Campanha"
              margin="normal"
              value={campaignForm.name}
              onChange={(e) => setCampaignForm({ ...campaignForm, name: e.target.value })}
            />
            <TextField
              fullWidth
              label="Descrição"
              margin="normal"
              multiline
              rows={3}
              value={campaignForm.description}
              onChange={(e) => setCampaignForm({ ...campaignForm, description: e.target.value })}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={campaignForm.status}
                onChange={(e) => setCampaignForm({ ...campaignForm, status: e.target.value as AICampaign['status'] })}
              >
                <MenuItem value="ativa">Ativa</MenuItem>
                <MenuItem value="pausada">Pausada</MenuItem>
                <MenuItem value="concluída">Concluída</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel>Estratégia IA</InputLabel>
              <Select
                label="Estratégia IA"
                value={campaignForm.aiStrategy.type}
                onChange={(e) => setCampaignForm({
                  ...campaignForm,
                  aiStrategy: {
                    ...campaignForm.aiStrategy,
                    type: e.target.value as AICampaign['aiStrategy']['type']
                  }
                })}
              >
                <MenuItem value="personalização">Personalização</MenuItem>
                <MenuItem value="segmentação">Segmentação</MenuItem>
                <MenuItem value="otimização">Otimização</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel>Público-Alvo</InputLabel>
              <Select
                multiple
                label="Público-Alvo"
                value={campaignForm.targetAudience}
                onChange={(e) => setCampaignForm({ ...campaignForm, targetAudience: e.target.value as string[] })}
              >
                <MenuItem value="novos">Novos Leads</MenuItem>
                <MenuItem value="contatados">Leads Contatados</MenuItem>
                <MenuItem value="convertidos">Leads Convertidos</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCampaignDialog(false)}>
            Cancelar
          </Button>
          <Button variant="contained" color="primary" onClick={handleSaveCampaign}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para Lead IA */}
      <Dialog
        open={openLeadDialog}
        onClose={() => setOpenLeadDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedLead ? 'Editar Lead IA' : 'Novo Lead IA'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Nome"
              margin="normal"
              value={leadForm.name}
              onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })}
            />
            <TextField
              fullWidth
              label="Email"
              margin="normal"
              type="email"
              value={leadForm.email}
              onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
            />
            <TextField
              fullWidth
              label="Telefone"
              margin="normal"
              value={leadForm.phone}
              onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={leadForm.status}
                onChange={(e) => setLeadForm({ ...leadForm, status: e.target.value as AILead['status'] })}
              >
                <MenuItem value="novo">Novo</MenuItem>
                <MenuItem value="contatado">Contatado</MenuItem>
                <MenuItem value="convertido">Convertido</MenuItem>
                <MenuItem value="perdido">Perdido</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel>Fonte</InputLabel>
              <Select
                label="Fonte"
                value={leadForm.source}
                onChange={(e) => setLeadForm({ ...leadForm, source: e.target.value })}
              >
                <MenuItem value="site">Site</MenuItem>
                <MenuItem value="redes_sociais">Redes Sociais</MenuItem>
                <MenuItem value="indicacao">Indicação</MenuItem>
                <MenuItem value="outros">Outros</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenLeadDialog(false)}>
            Cancelar
          </Button>
          <Button variant="contained" color="primary" onClick={handleSaveLead}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MkIAPage; 